/**
 * Backend API Client
 * 
 * All Firebase/Firestore operations are handled server-side.
 * The frontend communicates ONLY with the Rust backend via HTTP.
 */

const API_BASE = process.env.REACT_APP_API_URL || `https://${window.location.hostname || '192.168.0.102'}`;

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        throw new Error('Unable to connect to server. Please ensure the backend is running.');
      }
      throw err;
    }
  }

  // ─── Auth ────────────────────────────────────────────────────────────────

  /**
   * Verify a Firebase ID token with the backend.
   * @param {string} idToken - Firebase ID token
   * @returns {Promise<{success: boolean, uid: string, display_name: string|null, email: string|null, photo_url: string|null}>}
   */
  async verifyAuth(idToken) {
    return this.request('/api/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    });
  }

  // ─── Invites ─────────────────────────────────────────────────────────────

  /**
   * Create a new invite link.
   * @param {Object} params
   * @param {string} params.createdBy - The creator's user ID
   * @param {string} params.createdByName - The creator's display name
   * @returns {Promise<{invite_code: string}>}
   */
  async createInvite({ createdBy, createdByName }) {
    return this.request('/api/invites', {
      method: 'POST',
      body: JSON.stringify({
        created_by: createdBy,
        created_by_name: createdByName,
      }),
    });
  }

  /**
   * Get invite details by code.
   * @param {string} inviteCode
   * @returns {Promise<Object>} Invite data
   */
  async getInvite(inviteCode) {
    return this.request(`/api/invites/${inviteCode}`);
  }

  /**
   * Join an invite.
   * @param {string} inviteCode
   * @param {string} participantName
   * @param {string} [participantId] - Optional device-level guest ID for consistent identification
   * @returns {Promise<{success: boolean, conversation_id: string, participant_id: string, invite_data: Object}>}
   */
  async joinInvite(inviteCode, participantName, participantId) {
    const body = { participant_name: participantName };
    if (participantId) {
      body.participant_id = participantId;
    }
    return this.request(`/api/invites/${inviteCode}/join`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Deactivate an invite.
   * @param {string} inviteCode
   */
  async deactivateInvite(inviteCode) {
    return this.request(`/api/invites/${inviteCode}/deactivate`, {
      method: 'POST',
    });
  }

  // ─── Messages ────────────────────────────────────────────────────────────

  /**
   * Store a message in Firestore (for offline delivery).
   * @param {Object} params
   * @param {string} params.conversationId
   * @param {string} params.text
   * @param {string} params.senderId
   * @param {string} params.senderName
   * @param {string} params.sender - 'me' or 'them'
   * @param {string} [params.chatType] - 'my_chat' or 'anonymous'
   * @returns {Promise<{id: string, created_at: number}>}
   */
  async storeMessage({ conversationId, text, senderId, senderName, sender, chatType }) {
    return this.request('/api/messages', {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversationId,
        text,
        sender_id: senderId,
        sender_name: senderName,
        sender,
        chat_type: chatType,
      }),
    });
  }

  /**
   * Get all messages for a conversation.
   * @param {string} conversationId
   * @returns {Promise<Array>} Messages
   */
  async getMessages(conversationId) {
    return this.request(`/api/messages/${conversationId}`);
  }

  // ─── FCM / Push Notifications ────────────────────────────────────────────

  /**
   * Register an FCM token for push notifications.
   * @param {string} userId
   * @param {string} fcmToken
   */
  async registerFcmToken(userId, fcmToken) {
    return this.request('/api/fcm/register', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        fcm_token: fcmToken,
      }),
    });
  }

  /**
   * Send a push notification via FCM.
   * @param {Object} params
   * @param {string} params.targetUserId
   * @param {string} params.fcmToken
   * @param {string} params.title
   * @param {string} params.body
   * @param {string} params.conversationId
   */
  async sendPushNotification({ targetUserId, fcmToken, title, body, conversationId }) {
    return this.request('/api/fcm/send', {
      method: 'POST',
      body: JSON.stringify({
        target_user_id: targetUserId,
        fcm_token: fcmToken,
        title,
        body,
        conversation_id: conversationId,
      }),
    });
  }

  // ─── Online Status ───────────────────────────────────────────────────────

  /**
   * Check if a user is online.
   * Supports both registered and anonymous users.
   * @param {string} userId
   * @param {'registered'|'anonymous'} [kind='registered'] - The type of user
   * @returns {Promise<{online: boolean, active_connections: number, kind: string}>}
   */
  async checkUserOnline(userId, kind = 'registered') {
    return this.request(`/api/users/${userId}/online?kind=${kind}`);
  }

  /**
   * Get overall online statistics.
   * @returns {Promise<{registered_users_online: number, anonymous_users_online: number, total_unique_users: number, total_socket_connections: number}>}
   */
  async getOnlineStats() {
    return this.request('/api/online/stats');
  }
}

// Singleton instance
const api = new ApiClient();
export default api;
